import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Users, GraduationCap, ClipboardCheck, Wallet, Check, Rocket, ConciergeBell } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AriOrb from "@/components/ari/AriOrb";

const GOLD = "#D4AF37";
const EASE = [0.22, 1, 0.36, 1];
const SLOW = [0.16, 1, 0.3, 1];
const AriGlyph = () => <AriOrb size={15} state="insight" />;

const MODULES = [
  { key: "workforce",    icon: Users,          label: "Workforce",    tag: "One connected team.",           prog: 0.16 },
  { key: "training",     icon: GraduationCap,  label: "Training",     tag: "Learning without interruption.", prog: 0.37 },
  { key: "operations",   icon: ClipboardCheck, label: "Operations",   tag: "Every service under control.",   prog: 0.5 },
  { key: "guest",        icon: ConciergeBell,  label: "Guest Experience", tag: "A live concierge at every table.", prog: 0.68 },
  { key: "finance",      icon: Wallet,         label: "Cost Intelligence", tag: "Every receipt, captured and coded.",    prog: 0.84 },
  { key: "intelligence", icon: AriGlyph,   label: "ARI Intelligence", tag: "Your living intelligence, always on.",       prog: 1.0 },
];
const REVEAL = ["One Platform.", "Every Employee.", "Every Operation."];

const PILLARS = ["Reservations", "Guest Experience", "Service", "Regulars", "Feedback"];

// An elegant, premium hero block: a single graceful entrance (no looping "reboot"), a refined
// gold-shimmer headline, tightened subcopy, and a quiet row of the pillars ARI reads across.
// Reduced-motion safe; silent.
export const CinematicOS = ({ onReveal, onDone }) => {
  const reduce = useReducedMotion();
  const { t } = useLang();

  useEffect(() => { onReveal?.(); onDone?.(); }, [onReveal, onDone]);

  // 4D parallax: the whole headline stack tilts toward the cursor, and depth layers
  // (eyebrow / headline / copy / pillars) sit on different Z planes so the panel feels
  // sculpted and three-dimensional rather than flat.
  // IMPORTANT: only enable the tilt on real hover/fine-pointer devices. On touch, a tap or
  // scroll synthesises a mousemove (tilting the headline) but no mouseleave ever fires to reset
  // it — leaving the headline stuck at a slant. So touch devices keep it perfectly upright.
  const canHover = useRef(typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(hover: hover) and (pointer: fine)").matches : false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = { stiffness: 110, damping: 20, mass: 0.6 };
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), spring);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), spring);
  const onMove = (e) => {
    if (reduce || !canHover.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const rise3d = (delay) => reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.6, delay } }
    : { initial: { opacity: 0, y: 34, rotateX: -48 }, animate: { opacity: 1, y: 0, rotateX: 0 }, transition: { duration: 1.05, ease: EASE, delay } };

  const L1 = t("Run your restaurant").split(" ");

  return (
    <div className="relative p-7 sm:p-9 lg:p-11 h-full flex flex-col justify-center" data-testid="cinematic-os"
      onMouseMove={onMove} onMouseLeave={onLeave} style={{ perspective: 1200 }}>
      <motion.div className="relative" style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>

      {/* soft specular glow sitting behind the headline — gives the metal something to catch */}
      {!reduce && (
        <motion.div aria-hidden className="absolute -z-10 pointer-events-none rounded-full"
          style={{ left: "-6%", top: "8%", width: 420, height: 320, transform: "translateZ(-60px)",
            background: "radial-gradient(closest-side, rgba(212,175,55,0.18), rgba(212,175,55,0) 72%)", filter: "blur(10px)" }}
          animate={{ opacity: [0.5, 0.85, 0.5], scale: [1, 1.08, 1] }}
          transition={{ duration: 7.5, ease: "easeInOut", repeat: Infinity }} />
      )}

      {/* eyebrow */}
      <motion.div {...rise3d(0.04)} className="inline-flex items-center gap-3" style={{ transform: "translateZ(24px)" }}>
        <span className="relative inline-flex items-center justify-center" style={{ width: 12, height: 12 }}>
          {!reduce && (
            <motion.span aria-hidden className="absolute rounded-full"
              style={{ width: 12, height: 12, background: "radial-gradient(circle, rgba(251,236,161,0.55), rgba(212,175,55,0) 70%)" }}
              animate={{ scale: [1, 2.4, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 3.4, ease: "easeInOut", repeat: Infinity }} />
          )}
          <span className="relative rounded-full" style={{
            width: 7, height: 7,
            background: "radial-gradient(circle at 34% 28%, #FFF7D6 0%, #F3DC8E 32%, #D4AF37 62%, #9A7B22 100%)",
            boxShadow: "0 0 8px rgba(212,175,55,0.7), inset 0 0.5px 1px rgba(255,255,255,0.7)",
          }} />
        </span>
        <span className="font-mono-lux text-[10.5px] sm:text-[11px] tracking-[0.32em] uppercase" style={{ color: "#E9CE7B" }}>
          {t("The operating system for hospitality")}
        </span>
      </motion.div>

      {/* headline — line 1 reveals word-by-word in 3D, line 2 is living polished gold */}
      <h1 className="mt-7 font-serif-lux tracking-tight text-white text-[2.6rem] leading-[1.04] sm:text-6xl sm:leading-[1.0] lg:text-[4.6rem] lg:leading-[1.0]"
        style={{ transformStyle: "preserve-3d", transform: "translateZ(46px)" }}>
        <span className="block" style={{ transformStyle: "preserve-3d" }}>
          {L1.map((w, i) => (
            <motion.span key={`${w}-${i}`} className="inline-block" style={{ transformStyle: "preserve-3d", marginRight: "0.26em" }}
              {...rise3d(0.18 + i * 0.1)}>{w}</motion.span>
          ))}
        </span>
        <motion.span className="block gold-flow" style={{ transformStyle: "preserve-3d" }} {...rise3d(0.5)}>
          {t("on one intelligence.")}
        </motion.span>
      </h1>

      </motion.div>
    </div>
  );
};

const _CinematicOS_legacy = ({ onReveal, onDone }) => {
  const reduce = useReducedMotion();
  const { t } = useLang();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const [phase, setPhase] = useState(reduce ? "done" : "boot");
  const [revealStep, setRevealStep] = useState(0);

  // Calm, deliberate pacing — matched to the hero film's unrushed rhythm so each module can be
  // read and felt before the light moves on. Transitions are sequential (AnimatePresence wait).
  const D = isMobile
    ? { boot: 1400, mod: 3600, ws: 11000, intel: 8000, reveal: 4000, hold: 4800 }
    : { boot: 2200, mod: 5400, ws: 15000, intel: 10000, reveal: 5400, hold: 7000 };

  useEffect(() => {
    if (reduce) { onReveal?.(); onDone?.(); return; }
    const timers = [];
    let cancelled = false;

    const cycle = (first) => {
      if (cancelled) return;
      setRevealStep(0);
      if (first) setPhase("boot");
      const steps = [
        ["workforce", first ? D.boot : D.mod], ["training", D.mod], ["operations", D.mod],
        ["finance", D.mod], ["intelligence", D.mod], ["reveal", D.intel], ["done", D.reveal],
      ];
      let t = 0;
      steps.forEach(([name, dur]) => {
        t += dur;
        timers.push(setTimeout(() => {
          if (cancelled) return;
          setPhase(name);
          if (name === "reveal") onReveal?.();
          if (name === "done") onDone?.();
        }, t));
      });
      // hold on the resting headline, then loop the whole sequence again — forever.
      // Subsequent cycles skip the "boot" screen so the loop never feels like a reboot.
      t += D.hold;
      timers.push(setTimeout(() => { if (!cancelled) cycle(false); }, t));
    };

    cycle(true);
    return () => { cancelled = true; timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "reveal") return;
    const per = D.reveal / (REVEAL.length + 1);
    const timers = REVEAL.map((_, i) => setTimeout(() => setRevealStep(i + 1), per * (i + 1)));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const mod = MODULES.find((m) => m.key === phase);

  return (
    <div className="relative min-h-[360px] sm:min-h-[470px]" data-testid="cinematic-os">

      <AnimatePresence mode="wait">
        {/* BOOT */}
        {phase === "boot" && (
          <motion.div key="boot" className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.85, ease: SLOW }}>
            <p className="text-[11px] font-mono-lux tracking-[0.3em] uppercase text-[#D4AF37] inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] glow-pulse" /> The operating system for hospitality
            </p>
            <p className="mt-6 text-white/40 font-mono-lux tracking-[0.2em] uppercase text-xs">Activating intelligence…</p>
          </motion.div>
        )}

        {/* MODULE PHASES */}
        {mod && (
          <motion.div key={mod.key} className="absolute inset-0 max-w-xl"
            initial={{ opacity: 0, y: mod.key === "workshop" ? 22 : 14, scale: mod.key === "workshop" ? 0.985 : 1 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: mod.key === "workshop" ? -16 : -10, scale: mod.key === "workshop" ? 0.99 : 1 }}
            transition={{ duration: mod.key === "workshop" ? 1.6 : 1.0, ease: SLOW }}>
            <p className="text-[11px] font-mono-lux tracking-[0.3em] uppercase text-[#D4AF37] inline-flex items-center gap-2">
              <mod.icon className="w-3.5 h-3.5" /> {t(mod.label)}
            </p>
            <div data-testid="os-demo-card" className="mt-5 rounded-2xl border border-[#D4AF37]/22 bg-gradient-to-b from-[#17131D] to-[#0D0B11] p-5 h-36 sm:h-40 overflow-hidden shadow-[0_28px_66px_-28px_rgba(0,0,0,0.95)]">
              <ModuleViz kind={mod.key} />
            </div>
            <p data-testid="os-caption" className="font-serif-lux text-2xl sm:text-[2rem] leading-tight tracking-tight text-white mt-6">{t(mod.tag)}</p>
          </motion.div>
        )}

        {/* FINALE — one persistent headline across reveal + resting so the phrase
            "One platform. Every operation." renders exactly ONCE (no duplicate/overlap). */}
        {(phase === "reveal" || phase === "done") && (
          <motion.div key="finale" data-testid="os-reveal" className="absolute inset-0 max-w-xl"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.95, ease: SLOW }}>
            <motion.p className="text-[11px] font-mono-lux tracking-[0.3em] uppercase text-[#D4AF37] mb-5 inline-flex items-center gap-2"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] glow-pulse" /> {t("The operating system for hospitality")}
            </motion.p>
            <h1 className="font-serif-lux text-[2.3rem] leading-[1.02] sm:text-6xl sm:leading-[0.98] lg:text-[4.4rem] tracking-tight">
              <motion.span className="block" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.18 }}>{t("Run your restaurant")}</motion.span>
              <motion.span className="gold-shimmer block" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.34 }}>{t("on one intelligence.")}</motion.span>
            </h1>
            <motion.p className="text-white/70 text-base sm:text-lg mt-6 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.05, ease: EASE, delay: 0.62 }}>
              {t("Rota, training, attendance, payroll and receipts in one platform — with ARI reading across all of it, catching what needs you and recommending the next move before it costs you.")}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Distinct, bold Workshop scene that lingers and cycles real example capabilities so viewers
// actually SEE what can be built. Crisp vector graphics, vivid gold→teal ignition, 4D depth.
const WS_EXAMPLES = [
  { name: "Reservation", prompt: "Build a reservation tracker", fields: ["Guest name", "Party size", "Table", "Time"] },
  { name: "Guest CRM", prompt: "Track our regulars and their tastes", fields: ["Guest", "Visits", "Favourite dish", "Allergies"] },
  { name: "Prep Checklist", prompt: "Make an opening prep checklist", fields: ["Task", "Station", "Due by", "Done"] },
  { name: "Waste Log", prompt: "Log food waste and its cost", fields: ["Item", "Quantity", "Reason", "Cost"] },
];

export const WorkshopViz = () => {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setI((v) => (v + 1) % WS_EXAMPLES.length), 7200);
    return () => clearInterval(t);
  }, [reduce]);
  const ex = WS_EXAMPLES[i];
  return (
    <div className="relative h-full overflow-hidden rounded-lg" style={{ perspective: "1200px" }}>
      {/* crisp build grid */}
      <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.6) 1px,transparent 1px),linear-gradient(90deg,rgba(212,175,55,0.6) 1px,transparent 1px)", backgroundSize: "20px 20px" }} />
      <div className="absolute -inset-8 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 30% 40%, rgba(212,175,55,0.10), transparent 70%)" }} />
      {/* example counter dots */}
      <div className="absolute top-1 right-1 flex gap-1 z-10">
        {WS_EXAMPLES.map((_, d) => (
          <span key={d} className="w-1.5 h-1.5 rounded-full transition-colors duration-300" style={{ background: d === i ? GOLD : "rgba(255,255,255,0.18)", boxShadow: d === i ? `0 0 8px ${GOLD}` : "none" }} />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={i} className="relative h-full flex flex-col justify-center gap-3 px-0.5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8, ease: EASE }}
          style={{ willChange: "transform, opacity" }}>
          {/* sweeping build-beam re-fires per example */}
          <motion.div className="absolute inset-y-0 w-1/3 pointer-events-none"
            style={{ background: "linear-gradient(90deg,transparent,rgba(212,175,55,0.35),transparent)" }}
            initial={{ x: "-45%" }} animate={{ x: "270%" }} transition={{ duration: 1.9, ease: EASE, delay: 0.25 }} />
          {/* the command */}
          <motion.div className="inline-flex items-center gap-2 self-start"
            initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <span className="w-[3px] h-4 rounded-full" style={{ background: GOLD, boxShadow: `0 0 12px ${GOLD}` }} />
            <span className="font-mono-lux text-[10px] sm:text-[11.5px] tracking-[0.14em] uppercase text-white/90">{ex.prompt}</span>
            <motion.span className="w-[2px] h-3.5" style={{ background: GOLD }} animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} />
          </motion.div>
          {/* floating 4D capability tiles */}
          <div className="flex items-center gap-2" style={{ transformStyle: "preserve-3d" }}>
            {ex.fields.map((f, k) => (
              <motion.div key={f} className="flex-1 min-w-0 rounded-lg px-2 py-2.5 text-center"
                style={{ transformStyle: "preserve-3d", willChange: "transform, opacity", background: "linear-gradient(180deg, rgba(255,255,255,0.075), rgba(255,255,255,0.02))", border: "1px solid rgba(212,175,55,0.34)", boxShadow: "0 14px 30px -16px rgba(0,0,0,0.8), 0 0 16px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.10)" }}
                initial={{ opacity: 0, y: 24, rotateX: 38, z: -70 }} animate={{ opacity: 1, y: 0, rotateX: 0, z: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.55 + k * 0.2 }}>
                <span className="block text-[8.5px] sm:text-[9.5px] uppercase tracking-wide text-white/70 truncate">{f}</span>
                <motion.span className="mt-1 block h-1 rounded-full mx-auto" style={{ background: GOLD, width: "70%", boxShadow: `0 0 8px ${GOLD}` }}
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.55, ease: EASE, delay: 0.8 + k * 0.2 }} />
              </motion.div>
            ))}
          </div>
          {/* ignition: publishes LIVE */}
          <motion.div className="self-start inline-flex items-center gap-2 rounded-full px-3 py-1"
            style={{ border: "1px solid rgba(95,211,176,0.6)", background: "rgba(95,211,176,0.14)", boxShadow: "0 0 22px -6px rgba(95,211,176,0.6)" }}
            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, ease: EASE, delay: 1.7 }}>
            <Rocket className="w-3 h-3" style={{ color: "#5FD3B0" }} />
            <span className="font-mono-lux text-[10px] sm:text-[11.5px] tracking-[0.16em] uppercase font-semibold" style={{ color: "#8BF0CD" }}>{`${ex.name} · Published · Live`}</span>
            <motion.span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5FD3B0", boxShadow: "0 0 8px #5FD3B0" }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }} />
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};


const ModuleViz = ({ kind }) => {
  if (kind === "workforce") {
    const rows = [
      { n: "Sofia Marchetti", sh: "09:00 – 17:00", st: "Clocked in", on: true,  h: "7.5 / 8h" },
      { n: "James Okafor",    sh: "12:00 – 20:00", st: "Clocked in", on: true,  h: "5.0 / 8h" },
      { n: "Amara Singh",     sh: "17:00 – 23:00", st: "Scheduled",  on: false, h: "0 / 6h" },
    ];
    return (
      <div className="space-y-2 pt-0.5">
        {rows.map((r, i) => (
          <motion.div key={r.n} className="flex items-center gap-2.5"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}>
            <div className="w-6 h-6 rounded-full bg-white/12 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs text-white/90 truncate leading-tight" data-no-translate>{r.n}</p>
              <p className="text-[10px] text-white/40 font-mono-lux leading-tight">{r.sh}</p>
            </div>
            <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0"
              style={{ color: r.on ? GOLD : "rgba(255,255,255,0.45)", background: r.on ? "rgba(212,175,55,0.10)" : "rgba(255,255,255,0.05)", border: `1px solid ${r.on ? "rgba(212,175,55,0.35)" : "rgba(255,255,255,0.12)"}` }}>
              {r.st}
            </span>
            <span className="text-[10px] text-white/70 font-mono-lux tabular-nums shrink-0 w-12 text-right">{r.h}</span>
          </motion.div>
        ))}
      </div>
    );
  }
  if (kind === "training") {
    const rows = [
      { n: "James Okafor",    m: "Food Safety L2",    p: 100, s: "Passed · 96%" },
      { n: "Sofia Marchetti", m: "Allergen Handling", p: 72,  s: "72%" },
      { n: "Amara Singh",     m: "Fire Safety",       p: 45,  s: "45%" },
    ];
    return (
      <div className="space-y-2.5 pt-0.5">
        {rows.map((r, i) => (
          <motion.div key={r.m}
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-[11px] text-white/85 truncate"><span data-no-translate>{r.n}</span> · {r.m}</p>
              <span className="text-[10px] font-mono-lux shrink-0" style={{ color: r.p === 100 ? GOLD : "rgba(255,255,255,0.6)" }}>{r.s}</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/8 overflow-hidden">
              <motion.div className="h-full rounded-full" style={{ background: GOLD }}
                initial={{ width: "0%" }} animate={{ width: `${r.p}%` }} transition={{ duration: 1.1, ease: EASE, delay: i * 0.14 }} />
            </div>
          </motion.div>
        ))}
      </div>
    );
  }
  if (kind === "operations") {
    const rows = [
      { t: "Kitchen temperature log", st: "Complete", tone: "ok" },
      { t: "Allergen matrix review",  st: "Due 17:00", tone: "due" },
      { t: "Walk-in fridge at 6.4°C", st: "Flagged",   tone: "flag" },
    ];
    const dot = { ok: GOLD, due: "rgba(255,255,255,0.4)", flag: "#e0736d" };
    return (
      <div className="space-y-2.5 pt-1">
        {rows.map((r, i) => (
          <motion.div key={r.t} className="flex items-center gap-2.5"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot[r.tone] }} />
            <p className="text-[11px] sm:text-xs text-white/85 truncate flex-1">{r.t}</p>
            <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0"
              style={{ color: dot[r.tone], background: `${dot[r.tone]}1a`, border: `1px solid ${dot[r.tone]}55` }}>
              {r.st}
            </span>
          </motion.div>
        ))}
      </div>
    );
  }
  if (kind === "finance") {
    const rows = [
      { m: "Bidfood Wholesale", d: "12 Jun", c: "Stock",   tot: "£1,284.50" },
      { m: "Brakes Catering",   d: "11 Jun", c: "Produce", tot: "£642.10" },
    ];
    return (
      <div className="space-y-2 pt-0.5">
        {rows.map((r, i) => (
          <motion.div key={r.m} className="flex items-center gap-3"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[11px] sm:text-xs text-white/90 truncate" data-no-translate>{r.m}</p>
                <span className="text-[9px] inline-flex items-center gap-0.5 shrink-0" style={{ color: GOLD }}>
                  <Check className="w-2.5 h-2.5" /> Extracted
                </span>
              </div>
              <p className="text-[10px] text-white/40 font-mono-lux leading-tight">{r.d} · {r.c}</p>
            </div>
            <span className="text-xs text-white font-mono-lux tabular-nums shrink-0">{r.tot}</span>
          </motion.div>
        ))}
        <motion.div className="flex items-center justify-between pt-1.5 border-t border-white/8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, ease: EASE, delay: 0.4 }}>
          <span className="text-[10px] text-white/40 uppercase tracking-wide">Reconciled</span>
          <span className="text-[10px] font-mono-lux" style={{ color: GOLD }}>2 of 2 · £1,926.60</span>
        </motion.div>
      </div>
    );
  }
  if (kind === "guest") {
    const rows = [
      { tbl: "18", area: "Main Dining", req: "Bill requested", tone: "new" },
      { tbl: "T2", area: "Terrace", req: "Another still water", tone: "assigned" },
      { tbl: "PDR-1", area: "Private Dining", req: "Guest assistance", tone: "resolved" },
    ];
    const dot = { new: GOLD, assigned: "#B79CF2", resolved: "#8A9A5B" };
    const st = { new: "New", assigned: "Sarah", resolved: "Resolved" };
    return (
      <div className="space-y-2.5 pt-1">
        {rows.map((r, i) => (
          <motion.div key={r.tbl} className="flex items-center gap-2.5"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.14 }}>
            <span className="font-serif-lux text-base text-white/90 w-10 shrink-0" data-no-translate>{r.tbl}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs text-white/85 truncate leading-tight">{r.req}</p>
              <p className="text-[9px] text-white/40 leading-tight">{r.area}</p>
            </div>
            <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0"
              style={{ color: dot[r.tone], background: `${dot[r.tone]}1a`, border: `1px solid ${dot[r.tone]}55` }}>{st[r.tone]}</span>
          </motion.div>
        ))}
      </div>
    );
  }
  if (kind === "workshop") return <WorkshopViz />;
  return <IntelligenceViz />;
};

// ARI showcase: mirrors the REAL resourceful flow — a reference arrives, ARI reads it, states what
// it understood, builds a checklist from it, and proves the outcome. Procedural, loops silently.
const ARI_ITEMS = ["Count & reconcile till", "Card machine Z-report", "Sanitise surfaces", "Log fridge temps"];
// The raw, handwritten-style note ARI reads (real content) — later structured into ARI_ITEMS.
const NOTE_LINES = ["cash up + till float", "Z-report the card machine", "wipe down, log fridge temps", "bins out · lock up"];
const IntelligenceViz = () => {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0); // 0 reference in · 1 scanning · 2 understood + build · 3 live
  useEffect(() => {
    if (reduce) { setStep(3); return; }
    let cancelled = false;
    const timers = [];
    const run = () => {
      // Calm, readable pacing — the note holds long enough to read before ARI scans + structures it.
      [[0, 0], [1, 2800], [2, 5800], [3, 8400]].forEach(([s, t]) =>
        timers.push(setTimeout(() => { if (!cancelled) setStep(s); }, t)));
      timers.push(setTimeout(() => { if (!cancelled) run(); }, 12500));
    };
    run();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [reduce]);
  return (
    <div className="relative h-full flex flex-col justify-start pt-1 gap-2 px-0.5">
      <div className="absolute -inset-8 pointer-events-none" style={{ background: "radial-gradient(70% 70% at 40% 35%, rgba(212,175,55,0.09), transparent 72%)" }} />
      {/* the reference — a real closing note, blended into the panel (no boxed thumbnail) */}
      <div className="relative">
        <p className="font-mono-lux text-[9.5px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>
          {step === 0 ? "Reference received" : step === 1 ? "ARI reading it…" : "ARI understood"}
        </p>
        <AnimatePresence mode="wait">
          {step < 2 ? (
            <motion.div key="note" className="relative mt-1.5 space-y-1"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              {NOTE_LINES.map((ln, i) => (
                <motion.p key={ln} className="flex items-center gap-2.5 text-[10.5px] sm:text-[12px] leading-[1.5] text-white/80"
                  initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, ease: EASE, delay: 0.1 + i * 0.08 }}>
                  <span className="w-1 h-1 rounded-full shrink-0" style={{ background: GOLD, boxShadow: "0 0 6px rgba(212,175,55,0.6)" }} />
                  <span>{ln}</span>
                </motion.p>
              ))}
              {/* scan beam sweeping the real note — soft & feathered so it reads as light passing
                  over the note, never a hard gold line */}
              {step === 1 && (
                <motion.span className="absolute inset-x-0 h-10 pointer-events-none" style={{ background: "linear-gradient(180deg,transparent,rgba(212,175,55,0.16),transparent)", filter: "blur(5px)" }}
                  initial={{ top: "-30%" }} animate={{ top: "120%" }} transition={{ duration: 2.0, ease: EASE, repeat: Infinity }} />
              )}
            </motion.div>
          ) : (
            <motion.p key="built" className="text-[11px] sm:text-xs text-white/85 leading-tight mt-1"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              A closing routine — building a checklist
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      {/* the built checklist items */}
      <div className="space-y-1 pl-0.5">
        {ARI_ITEMS.map((it, i) => (
          <motion.div key={it} className="flex items-center gap-1.5"
            initial={{ opacity: 0, x: -6 }} animate={{ opacity: step >= 2 ? 1 : 0, x: step >= 2 ? 0 : -6 }}
            transition={{ duration: 0.4, ease: EASE, delay: step >= 2 ? i * 0.14 : 0 }}>
            <Check className="w-3 h-3 shrink-0" style={{ color: GOLD }} />
            <span className="text-[10px] sm:text-[11px] text-white/70 truncate">{it}</span>
          </motion.div>
        ))}
      </div>
      {/* outcome / live badge */}
      <motion.div className="self-start inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mt-0.5"
        style={{ border: "1px solid rgba(95,211,176,0.6)", background: "rgba(95,211,176,0.14)" }}
        initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: step >= 3 ? 1 : 0, scale: step >= 3 ? 1 : 0.7 }} transition={{ duration: 0.5, ease: EASE }}>
        <span className="font-mono-lux text-[9.5px] sm:text-[11px] tracking-[0.14em] uppercase font-semibold" style={{ color: "#8BF0CD" }}>Built · Assigned to team · Live</span>
        <motion.span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5FD3B0", boxShadow: "0 0 8px #5FD3B0" }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }} />
      </motion.div>
    </div>
  );
};
